import React, {useCallback, useMemo, useState} from "react";
import {Box, Chip, Typography} from "@mui/material";
import {useTranslation} from "react-i18next";
import Button from "../../../components/base/button/button";
import AnswerFormDialog from "../../../components/dialogs/answer-form-dialog";
import DateUtils from "../../../../core/services/date-utils";
import {FormResponseStatuses} from "../../../../packages/biz-form/core/constants/form-response-statuses";
import {formatFormResponseStatusLabel, getFormResponseStatusColor} from "../utils/form-response-status";
import {getActionablePartFormQualificationForms} from "../hooks/use-part-form-qualification";
import {useBizFormApiResolver} from "../../../hooks/use-biz-form-api-resolver";
import {useBizFileUrlsResolver} from "../../../hooks/use-biz-file-urls-resolver";

const closedDialogState = {
    open: false,
    newResponse: true,
};

/**
 * @param {object} props
 * @param {import("../../../../core/services/api/type-declerations/response").PartFormQualificationFormDs[]} props.forms
 * @param {boolean} [props.showPartLabels=false] When true, lists which parts await each form (merged batch UI).
 * @param {() => void | Promise<void>} props.onQualificationRefresh
 */
const PartFormComplianceCard = ({forms, showPartLabels = false, onQualificationRefresh}) => {
    const bizFormApiClient = useBizFormApiResolver((t) => t.operatorApp);
    const bizFileUrlChannel = useBizFileUrlsResolver((t) => t.bizFile);

    const {t} = useTranslation("components", {keyPrefix: "app-specific.part-form-compliance-card"});
    const [dialog, setDialog] = useState(closedDialogState);

    const sortedForms = useMemo(() => getActionablePartFormQualificationForms(forms), [forms]);

    const openFill = useCallback((form) => {
        setDialog({
            open: true,
            formId: form.formId,
            formTitle: form.formTitle,
            newResponse: true,
        });
    }, []);

    const openView = useCallback((form) => {
        if (!form.responseId) {
            return;
        }
        setDialog({
            open: true,
            formId: form.formId,
            formTitle: form.formTitle,
            formResponseId: form.responseId,
            newResponse: false,
        });
    }, []);

    const closeDialog = useCallback(() => {
        setDialog(closedDialogState);
    }, []);

    const handleCompleted = useCallback(() => {
        void onQualificationRefresh();
    }, [onQualificationRefresh]);

    if (!sortedForms.length) {
        return null;
    }

    return (
        <>
            <Box sx={{display: "flex", flexDirection: "column", gap: 2}}>
                <Typography variant="body2" textAlign="start">
                    {t(showPartLabels ? "introMultiple" : "intro")}
                </Typography>
                {sortedForms.map((form) => {
                    const hasResponse = !!form.responseId;
                    const status = form.responseStatus;
                    const comment = form.responseReviewComment;
                    const awaitingPartLabels = form.awaitingPartLabels ?? [];
                    return (
                        <Box
                            key={form.formId}
                            sx={{
                                border: 1,
                                borderColor: "divider",
                                borderRadius: 1,
                                p: 2,
                                display: "flex",
                                flexDirection: "column",
                                gap: 1,
                            }}
                        >
                            <Box sx={{display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 1}}>
                                <Box sx={{display: "flex", flexDirection: "column", gap: 0.5, flex: 1}}>
                                    <Typography variant="subtitle1" fontWeight={600}>
                                        {form.formTitle}
                                    </Typography>
                                    {showPartLabels && awaitingPartLabels.length > 0 && (
                                        <Box sx={{display: "flex", flexWrap: "wrap", gap: 0.5, alignItems: "center"}}>
                                            <Typography variant="body2" color="text.secondary" component="span">
                                                {t("awaitingPartsLabel")}
                                            </Typography>
                                            {awaitingPartLabels.map((partNumber) => (
                                                <Chip
                                                    key={partNumber}
                                                    size="small"
                                                    label={partNumber}
                                                    variant="outlined"
                                                />
                                            ))}
                                        </Box>
                                    )}
                                    {form.formDescription && (
                                        <Typography variant="body2" color="text.secondary">
                                            {form.formDescription}
                                        </Typography>
                                    )}
                                    {form.statusExpiryDate && (
                                        <Typography variant="body2" color="text.secondary">
                                            {t("expires", {
                                                date: DateUtils.serverToLocal(form.statusExpiryDate).format(DateUtils.defaultFormat),
                                            })}
                                        </Typography>
                                    )}
                                </Box>
                                {hasResponse && status && (
                                    <Chip
                                        size="medium"
                                        label={formatFormResponseStatusLabel(status)}
                                        color={getFormResponseStatusColor(status)}
                                    />
                                )}
                            </Box>
                            {hasResponse && status && comment && (
                                <Typography variant="body2" color={`${getFormResponseStatusColor(status)}.main`}>
                                    {t("reviewComment", {comment})}
                                </Typography>
                            )}
                            <Box sx={{display: "flex", gap: 1, flexWrap: "wrap", justifyContent: "center"}}>
                                {hasResponse && (
                                    <Button
                                        variant={status === FormResponseStatuses.Declined ? "outlined" : "contained"}
                                        color="primary"
                                        onClick={() => openView(form)}
                                    >
                                        {t("viewSubmission")}
                                    </Button>
                                )}
                                {(!hasResponse || status === FormResponseStatuses.Declined) && (
                                    <Button variant="contained" color="primary" onClick={() => openFill(form)}>
                                        {!hasResponse ? t("fillForm") : t("resubmitForm")}
                                    </Button>
                                )}
                            </Box>
                        </Box>
                    );
                })}
            </Box>
            {dialog.open && dialog.formId && (
                <AnswerFormDialog
                    open={dialog.open}
                    onClose={closeDialog}
                    formId={dialog.formId}
                    responseId={dialog.formResponseId}
                    newResponse={dialog.newResponse}
                    titleMode={dialog.newResponse ? "fill" : "view"}
                    api={bizFormApiClient}
                    fileInfo={bizFileUrlChannel.fileInfo}
                    onSuccess={handleCompleted}
                />
            )}
        </>
    );
};

export default PartFormComplianceCard;
